import { CUSTOM_ELEMENTS_SCHEMA, Component, ElementRef, AfterViewInit, OnDestroy, ViewChild, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { register } from 'swiper/element/bundle';

if (typeof window !== 'undefined' && !customElements.get('swiper-container')) {
  register();
}

@Component({
  selector: 'app-twigh-showcase',
  standalone: true,
  imports: [],
  templateUrl: './twigh-showcase.html',
  styleUrl: './twigh-showcase.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class TwighShowcaseComponent implements AfterViewInit, OnDestroy {
  @ViewChild('swiperEl') swiperEl!: ElementRef;
  @ViewChild('lgContainer') lgContainer!: ElementRef;

  private platformId = inject(PLATFORM_ID);
  private lgInstance: any = null;
  private lgSetupPromise: Promise<void> | null = null;

  prevSlide() { this.swiperEl.nativeElement.swiper.slidePrev(); }
  nextSlide() { this.swiperEl.nativeElement.swiper.slideNext(); }

  slides = [
    {
      title: 'Retail',
      subtitle: 'Curated storefronts that animate the avenue from morning through evening.',
      image: '/exterior/retail_3.webp',
      alt: 'Street-level retail frontage at Acacia'
    },
    {
      title: 'Outdoor Restaurant',
      subtitle: 'Open-air dining framed by warm lighting, planting, and a refined urban pulse.',
      image: '/amenities/new/restaurant6.webp',
      alt: 'Outdoor restaurant seating area at Acacia'
    },
    {
      title: 'Entrance',
      subtitle: 'A composed arrival experience that announces the property with confidence.',
      image: '/exterior/new/entrance2.webp',
      alt: 'Main entrance to Acacia'
    },
    {
      title: 'Lobby',
      subtitle: 'A calm reception lounge designed to feel tactile, intimate, and elevated.',
      image: '/amenities/new/reception5.webp',
      alt: 'Lobby and reception interior at Acacia'
    }
  ];

  readonly pagination = { clickable: true };
  readonly breakpoints = { 481: { slidesPerView: 1.12, spaceBetween: 20 } };

  readonly autoplay = {
    delay: 3600,
    disableOnInteraction: false,
    pauseOnMouseEnter: true
  };

  async ngAfterViewInit() {
    await this.ensureLightGallery();
  }

  async openAt(index: number, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    await this.ensureLightGallery();
    this.lgInstance?.openGallery(index);
  }

  ngOnDestroy() {
    this.lgInstance?.destroy();
  }

  private ensureLightGallery(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || this.lgInstance) {
      return Promise.resolve();
    }

    if (!this.lgSetupPromise) {
      this.lgSetupPromise = Promise.all([
        import('lightgallery'),
        import('lightgallery/plugins/zoom'),
        import('lightgallery/plugins/fullscreen')
      ]).then(([{ default: lightGallery }, { default: lgZoom }, { default: lgFullscreen }]) => {
        this.lgInstance = lightGallery(this.lgContainer.nativeElement, {
          plugins: [lgZoom, lgFullscreen],
          dynamic: true,
          dynamicEl: this.slides.map((slide) => ({
            src: slide.image,
            alt: slide.alt,
            subHtml: `<h4>${slide.title}</h4><p>${slide.subtitle}</p>`
          })),
          speed: 500,
          zoom: true,
          scale: 1,
          enableZoomAfter: 300,
          fullScreen: true,
          download: false,
          counter: true,
          backdropDuration: 300,
          mobileSettings: { controls: true, showCloseIcon: true, download: false }
        });
      });
    }

    return this.lgSetupPromise;
  }
}
